import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { ApiResponse } from 'hooks/api/api';
import { useMemoizedArrayData } from 'hooks/api/api';
import {
  AutomationsEmployeesService,
  type EmployeeAssistanceListQueryParams,
  type EmployeeAssistanceListResponse,
} from 'src/api/generated';

interface EmployeeAssistancesResponse extends ApiResponse {
  /** Loaded assistance records in API order. */
  assistances: EmployeeAssistanceListResponse['items'];
  /** Loads the next cursor page. */
  fetchNextPage: () => Promise<unknown>;
  /** Whether another cursor page exists. */
  hasNextPage: boolean;
  /** Whether the next cursor page is loading. */
  isFetchingNextPage: boolean;
  /** Whether pagination stopped because the API repeated a cursor. */
  listIncomplete: boolean;
}

/**
 * Fetches the tenant-wide AI Agent assistance projection.
 *
 * @param params - Cursor pagination and assistance filter parameters.
 * @returns Assistance records and the query state used to paginate or retry.
 */
export const useEmployeeAssistances = (
  params: EmployeeAssistanceListQueryParams
): EmployeeAssistancesResponse => {
  const {
    limit = 20,
    status,
    employeeId,
    category,
    conversationId,
    from,
    to,
  } = params;

  const query = useInfiniteQuery({
    queryKey: ['automations/employees/assistances', params],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      AutomationsEmployeesService.listAssistances(
        limit,
        pageParam,
        status,
        employeeId,
        category,
        conversationId,
        undefined,
        from,
        to
      ),
    getNextPageParam: (
      lastPage: EmployeeAssistanceListResponse,
      _allPages: EmployeeAssistanceListResponse[],
      _lastPageParam: string | undefined,
      allPageParams: (string | undefined)[]
    ) => {
      const nextPageToken = lastPage.nextPageToken;

      return nextPageToken && !allPageParams.includes(nextPageToken)
        ? nextPageToken
        : undefined;
    },
    initialPageParam: undefined,
  });

  const assistances = useMemoizedArrayData(
    Array.from(
      new Map(
        (query.data?.pages.flatMap((page) => page.items) ?? []).map(
          (assistance) => [assistance.assistanceId, assistance]
        )
      ).values()
    )
  );
  const nextPageToken = query.data?.pages.at(-1)?.nextPageToken;
  const pageParams = query.data?.pageParams ?? [];
  const listIncomplete = Boolean(
    nextPageToken && pageParams.includes(nextPageToken)
  );

  return {
    assistances,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    listIncomplete,
  };
};

interface EmployeeAssistanceResolutionMutation {
  /** Resolves one visible assistance. */
  resolveAssistance: (assistanceId: string) => void;
  /** Assistance currently being resolved, when a request is active. */
  resolvingAssistanceId?: string;
  /** Latest resolution error. */
  error: Error | null;
}

/**
 * Resolves one visible assistance and refreshes every active assistance list.
 *
 * @returns Resolution action, pending assistance identifier, and error state.
 */
export const useResolveEmployeeAssistance =
  (): EmployeeAssistanceResolutionMutation => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
      mutationFn: (assistanceId: string) =>
        AutomationsEmployeesService.resolveAssistance(assistanceId),
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['automations/employees/assistances'],
        });
      },
    });

    return {
      resolveAssistance: mutation.mutate,
      resolvingAssistanceId: mutation.isPending
        ? mutation.variables
        : undefined,
      error: mutation.error,
    };
  };
